import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, CheckCircle2, Circle, Lock, Clock, PartyPopper } from "lucide-react";
import AppShell from "@/components/AppShell";
import { ReadinessBar, FullPageLoader } from "@/components/Common";
import { TestimonialInvite } from "@/components/TestimonialInvite";
import { api } from "@/lib/api";

const STATUS_ICON = {
  completed: <CheckCircle2 size={18} className="text-emerald-500" />,
  in_progress: <Clock size={18} className="text-brand" />,
  available: <Circle size={18} className="text-brand-muted" />,
  locked: <Lock size={16} className="text-slate-300" />,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, []);

  if (!data) return <FullPageLoader label="Opening your workspace…" />;
  if (data.error || !data.business) return <FullPageLoader label="Setting things up…" />;

  const { business, focus, roadmap, readiness, assets_count, asset_names } = data;
  const firstName = (business.name || "there").split(" ")[0];

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-4xl px-8 py-10 ltifade" data-testid="student-dashboard">
        <p className="text-sm text-slate-500">Welcome back</p>
        <h1 className="mt-0.5 font-heading text-2xl font-semibold tracking-tight">Here's what to work on today</h1>

        <div className="mt-6"><TestimonialInvite /></div>

        {/* dominant focus card */}
        {focus ? (
          <div
            className="mt-6 rounded-2xl border-2 border-brand/30 bg-white p-7 shadow-soft"
            data-testid="focus-card"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-muted">
              Today's focus · step {focus.order} of 7
            </p>
            <h2 className="mt-2 font-heading text-2xl font-semibold tracking-tight">{focus.title}</h2>
            <p className="mt-2 max-w-xl leading-relaxed text-slate-600">{focus.summary}</p>
            <p className="mt-1 text-sm text-slate-400">About {focus.est_minutes} minutes · produces your {focus.asset_name}</p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <button
                data-testid="start-task-btn"
                onClick={() => navigate(`/task/${focus.slug}`)}
                className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover hover:shadow-lift"
              >
                {focus.status === "in_progress" ? "Resume task" : "Start task"} <ArrowRight size={16} />
              </button>
              <span className="text-sm text-slate-400">Your draft saves automatically</span>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border-2 border-emerald-200 bg-white p-7 shadow-soft" data-testid="focus-card">
            <PartyPopper size={22} className="text-emerald-500" />
            <h2 className="mt-2 font-heading text-2xl font-semibold">Every step is done</h2>
            <p className="mt-2 text-slate-600">You've built all seven assets. Your business is ready to launch. Head to the Launch Center to go live.</p>
            <button onClick={() => navigate("/launch")} className="mt-4 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white">Open Launch Center</button>
          </div>
        )}

        {/* readiness + assets */}
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <ReadinessBar label="Business readiness" value={readiness?.business_readiness ?? 0} />
          <ReadinessBar label="Launch readiness" value={readiness?.launch_readiness ?? 0} />
          <div className="rounded-xl border border-brand-line bg-white p-4 shadow-soft" data-testid="assets-built-stat">
            <p className="text-sm text-slate-500">Assets built</p>
            <p className="mt-0.5 font-heading text-2xl font-semibold">{assets_count}</p>
            <p className="mt-2 truncate text-xs text-slate-400">{asset_names?.join(", ") || "None yet"}</p>
          </div>
        </div>

        {/* quiet AI entry */}
        <div className="mt-5 flex items-center justify-between rounded-xl border border-brand-line bg-brand-surface/40 px-4 py-3">
          <p className="text-sm text-slate-600">
            Stuck on a step?{" "}
            <button onClick={() => navigate("/ai-studio")} className="font-medium text-brand-text hover:underline" data-testid="ai-entry-link">
              Ask AI Studio for a first draft
            </button>{" "}
            · marked as AI assisted
          </p>
          <Sparkles size={16} className="text-brand" />
        </div>

        {/* roadmap / assets */}
        <h3 className="mt-9 font-heading text-lg font-medium">Your business assets</h3>
        <p className="mt-1 text-sm text-slate-500">Seven assets make up your business. Pick up any unlocked one anytime. It is all free during early access.</p>
        <div className="mt-3 overflow-hidden rounded-xl border border-brand-line bg-white shadow-soft" data-testid="roadmap-list">
          {roadmap.map((t) => {
            const done = t.status === "completed";
            const actionable = t.status === "available" || t.status === "in_progress";
            return (
            <button
              key={t.slug}
              disabled={t.status === "locked"}
              onClick={() => t.status !== "locked" && navigate(`/task/${t.slug}`)}
              data-testid={`roadmap-item-${t.slug}`}
              className={`flex w-full items-center gap-3 border-b border-brand-line px-4 py-3.5 text-left last:border-0 transition-all duration-200 ${
                t.status === "locked" ? "cursor-not-allowed opacity-60" : "hover:bg-brand-surface/40"
              }`}
            >
              <span className="shrink-0">{STATUS_ICON[t.status]}</span>
              <span className="w-5 shrink-0 text-sm text-slate-400">{t.order}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{t.asset_name}</span>
                  <span className="shrink-0 text-xs font-semibold text-slate-500" data-testid={`asset-completion-${t.slug}`}>{t.completion}%</span>
                </span>
                <span className="mt-0.5 block truncate text-xs text-slate-400">{t.title}</span>
                <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-brand-surface">
                  <span
                    className={`block h-full rounded-full transition-all duration-500 ${done ? "bg-emerald-500" : "bg-brand"}`}
                    style={{ width: `${t.completion}%` }}
                  />
                </span>
              </span>
              <span className="ml-1 hidden shrink-0 items-center gap-1 text-xs font-medium sm:inline-flex">
                {done ? (
                  <span className="text-emerald-600">Completed</span>
                ) : actionable ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-white">
                    {t.status === "in_progress" ? "Continue" : "Start"} <ArrowRight size={12} />
                  </span>
                ) : (
                  <span className="text-slate-300">Locked</span>
                )}
              </span>
            </button>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
